/**
 * Чип фильтра (§2.3 «<FilterChip>», §10.1).
 *
 * Три состояния задаёт владелец фильтров, чип их только показывает:
 * `off` · `on` · `excluded`. Клик и клавиатура различают три намерения, потому
 * что алгебра фильтров их различает (§10.1: внутри группы — или, между
 * группами — и):
 *
 *   клик            / Enter, Space  → `replace` — заменить выбор этим значением
 *   Shift+клик      / Shift+Enter   → `add`     — добавить к выбору
 *   Alt+клик        / Alt+Enter     → `exclude` — исключить значение
 *
 * Счётчик — обязательная часть чипа: у каждой цифры на экране написана её
 * вселенная (П7), и пустой чип без числа интерфейсу не нужен.
 */
import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import './Chip.css'

export type ChipState = 'off' | 'on' | 'excluded'
export type ChipIntent = 'replace' | 'add' | 'exclude'

export interface ChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onClick'> {
  label: string
  /** Число в этом срезе. 0 показывается: «ноль» — тоже ответ (П7). */
  count?: number
  state?: ChipState
  icon?: ReactNode
  onToggle?: (intent: ChipIntent) => void
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { label, count, state = 'off', icon, onToggle, disabled = false, className, ...rest },
  ref,
) {
  const classes = ['chip', 't-badge', `chip--${state}`]
  if (className) classes.push(className)

  function intentOf(event: { shiftKey: boolean; altKey: boolean }): ChipIntent {
    if (event.altKey) return 'exclude'
    if (event.shiftKey) return 'add'
    return 'replace'
  }

  return (
    <button
      ref={ref}
      type="button"
      className={classes.join(' ')}
      aria-pressed={state === 'on'}
      aria-disabled={disabled || undefined}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault()
          return
        }
        onToggle?.(intentOf(event))
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        if (disabled) return
        onToggle?.(intentOf(event))
      }}
      {...rest}
    >
      {state === 'excluded' ? (
        <span className="chip__sign" aria-hidden="true">
          −
        </span>
      ) : null}
      {icon ? <span className="chip__icon">{icon}</span> : null}
      <span>{label}</span>
      {count != null ? <span className="chip__count">{count}</span> : null}
      {state === 'excluded' ? <span className="visually-hidden">исключён</span> : null}
    </button>
  )
})
